<div class="tab-content group-management">
  <!-- RBAC Teaser Banner for non-RBAC accounts -->
  @if (!rbacFullEnabled) {
    <div class="rbac-teaser-banner">
      <i class="fa-utility fa-semibold fa-lock"></i>
      <span>
        <strong>Unlock Group-Based Access Control</strong> — Organize users into groups for easier role management.
        Contact your account manager to enable this feature.
      </span>
    </div>
  }

  @if (rbacFullEnabled) {
    <div>Organize access by creating groups that combine roles and applications.</div>
  }
  <div class="flex justify-between margin-top">
    <div class="flex items-center">
      <h2>All Groups</h2>
      @if (canManageGroups$ | async) {
        <a class="underline margin-left" (click)="openNewGroupModal()" data-cy="create-group-button">
          <i class="fa-utility fa-semibold fa-plus text-sm"></i>Create New Group
        </a>
      }
    </div>
    <div class="half-margin-bottom">
      <div class="filter-container">
        <input pInputText #filterInput type="text" (input)="filterGlobal($event)" placeholder="Search..." />
        @if (loadingFilterInput) {
          <div class="fa-solid fa-spinner fa-spin"></div>
        }
        @if (!isFilterApplied && !loadingFilterInput) {
          <div class="fa-utility-fill fa-semibold fa-filter fade-icon"></div>
        }
        @if (isFilterApplied && !loadingFilterInput) {
          <div (click)="clearInput()" class="fa-utility fa-semibold fa-xmark"></div>
        }
      </div>
    </div>
  </div>
  <!-- Unauthorized State -->
  @if ((canViewGroups$ | async) === false) {
    <div class="unauthorized-container" data-cy="unauthorized-message">
      <div class="unauthorized-content">
        <h3>Access Restricted</h3>
        <p>{{ unauthorizedMessage$ | async }}</p>
      </div>
    </div>
  }

  <!-- Loading State - show while data hasn't loaded yet -->
  @if (groups === null) {
    <div class="loading-container"><i class="fa-solid fa-spinner fa-spin"></i> Loading groups...</div>
  }

  <!-- Authorized Content - only show when groups array exists (even if empty) -->
  @if (groups !== null && (canViewGroups$ | async) !== false) {
    <div class="table-container">
      <!-- Empty State -->
      @if (groups.length === 0) {
        <div class="empty-state-container">
          <div class="empty-state-content">
            <h3>No groups have been created yet.</h3>
            <p>
              Groups allow you to assign permissions at scale by bundling roles with applications. Create your first
              group to streamline access management.
            </p>
            @if (canManageGroups$ | async) {
              <button class="p-button p-button-primary" (click)="openNewGroupModal()">
                <i class="fa-utility fa-semibold fa-plus"></i>
                Create Your First Group
              </button>
            }
          </div>
        </div>
      }
      <!-- Table Section - Only show when groups exist -->
      @if (groups.length > 0) {
        <div class="table-section main-table">
          <p-table
            #dt1
            [value]="groups"
            data-cy="group-table"
            [globalFilterFields]="globalFilterFields"
            dataKey="id"
            [scrollable]="true"
            scrollHeight="60vh"
            [tableStyle]="{ 'min-width': '30rem' }"
            [styleClass]="'p-datatable-sm arp-prime-datatable'"
            [rowHover]="true"
            [totalRecords]="groups?.length"
            [paginator]="false"
            [rowTrackBy]="groupTrackBy"
            selectionMode="single"
            [(selection)]="selectedTableItem"
            (onRowSelect)="onRowSelect($event.data)"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="name" class="strong-text name-column">
                  Name <p-sortIcon field="name"></p-sortIcon>
                </th>
                <th pSortableColumn="roles" class="strong-text roles-column">
                  Roles <p-sortIcon field="roles"></p-sortIcon>
                </th>
                <th pSortableColumn="assignedUsersCount" class="strong-text user-count-column">
                  # Assigned Users <p-sortIcon field="assignedUsersCount"></p-sortIcon>
                </th>
                <th pSortableColumn="lastModified" class="strong-text last-modified-column">
                  Last Modified <p-sortIcon field="lastModified"></p-sortIcon>
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-group>
              <tr
                [pSelectableRow]="group"
                class="cursor-pointer"
                [ngClass]="{
                  'selected-row': showSidePanel && selectedGroup && selectedGroup.id === group.id,
                  'refreshing-row': isGroupRefreshing(group),
                }"
              >
                <td class="name-column" style="position: relative">
                  <div class="group-info">
                    <div class="rbac-table-display-container">
                      @if (isGroupRefreshing(group)) {
                        <i class="fa-solid fa-spinner fa-spin half-margin-right"></i>
                      }
                      <span>{{ group.name }}</span>
                    </div>
                  </div>
                </td>
                <td class="roles-column">
                  <div class="roles-list">
                    @if (isGroupRefreshing(group)) {
                      <i class="fa-solid fa-spinner fa-spin half-margin-right"></i>
                    }
                    @if (group.roles.length === 0) {
                      <span><em>None</em></span>
                    }
                    @for (role of getSortedGroupRolesLimited(group, 2); track role) {
                      <a
                        [routerLink]="['/' + accountService.accountId + '/settings/role-management']"
                        [queryParams]="{ selectRole: role.name }"
                        class="role-tag-link"
                      >
                        <p-tag severity="info" class="role-tag clickable-tag fit-width" value="{{ role.name }}">
                        </p-tag>
                      </a>
                    }
                    @if (group.roles.length > 2) {
                      <span
                        class="more-count"
                        [pTooltip]="getAdditionalRolesTooltip(group)"
                        tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                        tooltipPosition="top"
                        [autoHide]="false"
                        [hideDelay]="200"
                        [escape]="false"
                        tooltipEvent="hover"
                      >
                        +{{ group.roles.length - 2 }} more
                      </span>
                    }
                  </div>
                </td>
                <td class="user-count-column">
                  <div class="user-count-display">
                    <span class="user-count-badge">{{ group.assignedUsersCount }}</span>
                  </div>
                </td>
                <td class="last-modified-column">
                  {{ getRelativeTimeDisplay(group.lastModified) }}
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4" class="empty-state-cell text-center">
                  <div class="empty-state-content">
                    <p class="empty-state-message">No results found</p>
                    <p class="empty-state-submessage">Try adjusting your search or filter criteria.</p>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="summary">
              <div class="flex align-items-center justify-content-between">
                <span
                  >Displaying {{ dt1.filteredValue?.length ?? dt1.value?.length }} of
                  {{ dt1.value?.length }} groups</span
                >
              </div>
            </ng-template>
          </p-table>
        </div>
      }
      <!-- Side Panel -->
      <div class="side-panel" [ngClass]="{ open: showSidePanel }" #sidePanel>
        <div class="side-panel-header">
          <div class="flex items-center">
            <button class="close-btn" (click)="closeSidePanel()">
              <i class="fa-utility fa-semibold fa-angle-right"></i>
            </button>
            <h3>Group Details</h3>
          </div>
          <div>
            @if (canManageGroups$ | async) {
              <button class="edit-button" (click)="openEditGroupModal()">
                <i class="fa-utility fa-semibold fa-pen-to-square"></i>
              </button>
            }
          </div>
        </div>
        <div class="side-panel-content">
          <!-- Basic Information Section -->
          <div class="detail-section">
            <div class="detail-item">
              <label>Name:</label>
              @if (showSidePanel && selectedGroup) {
                <span>{{ selectedGroup.name }}</span>
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="detail-skeleton"></div>
              }
            </div>
            @if (selectedGroup?.externalId || !showSidePanel) {
              <div class="detail-item">
                <label>External ID:</label>
                @if (showSidePanel && selectedGroup?.externalId) {
                  <span>{{ selectedGroup.externalId }}</span>
                }
                @if (showSidePanel && selectedGroup && !selectedGroup.externalId) {
                  <span class="text-muted">Not set</span>
                }
                @if (!showSidePanel || !selectedGroup) {
                  <div class="detail-skeleton"></div>
                }
              </div>
            }
          </div>
          <div class="detail-section">
            @if (showSidePanel && selectedGroup) {
              <h4>Assigned Roles ({{ selectedGroup.roles.length }})</h4>
            }
            @if (!showSidePanel || !selectedGroup) {
              <h4>Roles</h4>
            }
            <div class="roles-list detail-item">
              @if (showSidePanel && selectedGroup) {
                @for (role of getSortedGroupRoles(); track role) {
                  <a
                    [routerLink]="['/' + accountService.accountId + '/settings/role-management']"
                    [queryParams]="{ selectRole: role.name }"
                    class="role-tag-link"
                  >
                    <p-tag severity="info" class="role-tag clickable-tag fit-width" value="{{ role.name }}"> </p-tag>
                  </a>
                }
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="tag-skeleton" style="margin-right: 0.25rem"></div>
                <div class="tag-skeleton" style="margin-right: 0.25rem"></div>
              }
            </div>
            <!-- No roles message -->
            @if (showSidePanel && selectedGroup && selectedGroup.roles.length === 0) {
              <div class="detail-item">
                <em class="no-data-text">No roles assigned to this group</em>
              </div>
            }
          </div>
          <!-- Policies Section -->
          <div class="detail-section">
            <h4>Policies ({{ getGroupPolicies().length }})</h4>
            <div class="detail-item">
              @if (showSidePanel && selectedGroup && getGroupPolicies().length > 0) {
                <div class="policies-list">
                  @for (policy of getGroupPolicies(); track policy; let i = $index) {
                    <div class="policy-item">
                      <div class="policy-header half-half-margin-bottom">
                        <h5>Policy {{ i + 1 }}</h5>
                      </div>
                      <div class="policy-description text-sm text-muted margin-bottom">
                        @if (getPolicyDescriptionComponents(policy); as components) {
                          <p-tag
                            severity="info"
                            class="access-level-tag-display fit-width"
                            style="display: inline-flex; margin-right: 0.25rem"
                            value="{{ components.accessLevel }}"
                          >
                          </p-tag
                          >{{ components.prefix }}
                          @if (components.showTooltip) {
                            <span
                              [pTooltip]="components.tooltipText"
                              tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                              tooltipPosition="top"
                              [autoHide]="false"
                              [hideDelay]="200"
                              [escape]="false"
                              tooltipEvent="hover"
                              class="text-underline-dotted"
                              >{{ components.applicationsText }}</span
                            >
                          }
                          @if (!components.showTooltip) {
                            <span>{{ components.applicationsText }}</span>
                          }
                          {{ components.suffix }}
                        }
                      </div>
                      @if (getInheritedFromText(policy)) {
                        <div class="policy-inheritance text-xs text-muted">
                          <em>{{ getInheritedFromText(policy) }}</em>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
              @if (showSidePanel && selectedGroup && getGroupPolicies().length === 0) {
                <div class="no-data-text">No policies defined</div>
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="detail-skeleton"></div>
              }
            </div>
          </div>
          <!-- Assigned Users Section -->
          <div class="detail-section">
            @if (showSidePanel && selectedGroup) {
              <h4>Assigned Users ({{ selectedGroup.assignedUsersCount }})</h4>
            }
            @if (!showSidePanel || !selectedGroup) {
              <h4>Assigned Users</h4>
            }
            <!-- Search bar for assigned users -->
            @if (showSidePanel && selectedGroup && selectedGroup.assignedUsers.length > 10) {
              <div class="users-search-container margin-top">
                <input
                  pInputText
                  type="text"
                  #usersSearchInput
                  (input)="filterAssignedUsers($event)"
                  placeholder="Search users..."
                  class="arpio-input users-search"
                />
                <div class="fa-utility fa-semibold fa-magnifying-glass search-icon"></div>
              </div>
            }
            <div class="users-list">
              @if (showSidePanel && selectedGroup) {
                @for (user of getFilteredAssignedUsers(); track user) {
                  <a
                    [routerLink]="['/' + accountService.accountId + '/settings/user-management']"
                    [queryParams]="{ selectUser: user.accountSubjectId }"
                    class="user-item clickable-user"
                    [class.sso-user]="isUserSsoAssigned(user)"
                  >
                    @if (!isUserSsoAssigned(user)) {
                      <i class="fa-utility-fill fa-semibold fa-user"></i>
                    }
                    @if (isUserSsoAssigned(user)) {
                      <i class="fa-utility fa-semibold fa-id-card sso-icon"></i>
                    }
                    <span>{{ user.name }}</span>
                  </a>
                }
                <!-- No users message -->
                @if (selectedGroup.assignedUsers && selectedGroup.assignedUsers.length === 0) {
                  <div class="detail-item">
                    <em class="no-data-text">No users assigned to this group</em>
                  </div>
                }
                <!-- No search results message -->
                @if (
                  selectedGroup.assignedUsers &&
                  selectedGroup.assignedUsers.length > 0 &&
                  getFilteredAssignedUsers().length === 0 &&
                  usersSearchText
                ) {
                  <div class="no-data-text">No users found matching "{{ usersSearchText }}"</div>
                }
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="user-item">
                  <i class="fa-utility-fill fa-semibold fa-user"></i>
                  <div class="detail-skeleton" style="width: 200px"></div>
                </div>
                <div class="user-item">
                  <i class="fa-utility-fill fa-semibold fa-user"></i>
                  <div class="detail-skeleton" style="width: 180px"></div>
                </div>
                <div class="user-item">
                  <i class="fa-utility-fill fa-semibold fa-user"></i>
                  <div class="detail-skeleton" style="width: 220px"></div>
                </div>
              }
            </div>
          </div>
          <!-- Additional Information Section -->
          <div class="detail-section">
            <h4>Additional Information</h4>
            <div class="detail-item">
              <label>Created:</label>
              @if (showSidePanel && selectedGroup) {
                <span>{{ selectedGroup.createdAt | date: 'medium' }}</span>
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="detail-skeleton"></div>
              }
            </div>
            <div class="detail-item">
              <label>Last Modified:</label>
              @if (showSidePanel && selectedGroup) {
                <span>{{ selectedGroup.lastModified | date: 'medium' }}</span>
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="detail-skeleton"></div>
              }
            </div>
          </div>
          <!-- Action Buttons Section -->
          @if (showSidePanel && selectedGroup && (canManageGroups$ | async)) {
            <div class="detail-section action-buttons">
              <h4>Actions</h4>
              <div class="margin-auto text-center margin-top">
                <a class="delete-group-button text-center" (click)="deleteGroup()">Delete Group...</a>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <ng-template #loadingBlock> <i class="fa-solid fa-spinner fa-spin"></i> Loading groups... </ng-template>

  <app-new-group-modal #newGroupModal (groupCreated)="onGroupCreated()"></app-new-group-modal>
  <app-edit-group-modal #editGroupModal (groupUpdated)="onGroupUpdated($event)"></app-edit-group-modal>

  <!-- Delete Group Confirmation Modal -->
  <modal-dialog #deleteGroupDialog [dialogTemplate]="confirmDeleteGroupDialog"></modal-dialog>
  <ng-template #confirmDeleteGroupDialog>
    <confirm-action-dialog
      name="&quot;{{ groupToDelete?.name }}&quot;"
      action="Delete"
      [error]="deleteGroupDialog.error"
      (onCancel)="cancelDeleteGroup()"
      (onAction)="confirmDeleteGroup()"
    >
      <div class="error margin-top">
        Deleting this group will remove it from all assigned users. This action cannot be undone.
      </div>
    </confirm-action-dialog>
  </ng-template>
</div>
